import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Printer, Receipt, Send, Trash2, Wallet } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  Callout,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Loading,
  Modal,
  Select,
  Textarea,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { BUCKET_TONE, dateLabel, money, num, phone, titleCase } from '../lib/format.js';
import BrandMark from '../components/BrandMark.jsx';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/invoices/${id}/detail`, { deps: [id] });
  const [payOpen, setPayOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading && !data) {
    return (
      <>
        <PageHead title="Invoice" subtitle="Loading…" />
        <Loading rows={6} />
      </>
    );
  }
  if (!data?.invoice) {
    return (
      <Card>
        <EmptyState icon={Receipt} title="Invoice not found">
          <Link to="/app/invoices">Back to invoices</Link>
        </EmptyState>
      </Card>
    );
  }

  const { invoice, customer, contacts, items, payments } = data;
  const contact = contacts.find((c) => c.isPrimary) || contacts[0];

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${id}/send`);
      toast.ok(`${invoice.code} sent`, customer?.billingEmail ? `Emailed to ${customer.billingEmail}` : undefined);
      refresh();
    } catch (err) {
      toast.err('Could not send the invoice', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        breadcrumb={[
          { label: 'Invoices', to: '/app/invoices' },
          { label: customer?.name, to: `/app/customers/${invoice.customerId}` },
          { label: invoice.code },
        ]}
        title={invoice.code}
        subtitle={
          <span className="row gap-10 wrap">
            <Badge tone={invoice.status === 'paid' ? 'green' : invoice.status === 'overdue' ? 'red' : 'blue'}>
              {titleCase(invoice.status)}
            </Badge>
            <Badge tone={BUCKET_TONE[invoice.agingBucket] || 'muted'}>{invoice.agingBucket}</Badge>
            <span className="dim">{customer?.name}</span>
            <span className="dim">Issued {dateLabel(invoice.issuedAt)} · due {dateLabel(invoice.dueAt)}</span>
            {invoice.daysPastDue > 0 ? <Badge tone="red">{invoice.daysPastDue} days past due</Badge> : null}
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" icon={Printer} onClick={() => window.print()}>Print</Button>
            {invoice.status === 'draft' ? (
              <Button variant="outline" size="sm" icon={Send} loading={busy} onClick={send}>Send to customer</Button>
            ) : null}
            {Number(invoice.balance) > 0 ? (
              <Button variant="primary" size="sm" icon={Wallet} onClick={() => setPayOpen(true)}>Record payment</Button>
            ) : null}
            {invoice.status !== 'paid' ? (
              <Button variant="danger" size="sm" icon={Trash2} onClick={() => setDeleteOpen(true)}>Delete</Button>
            ) : null}
          </>
        }
      />

      {invoice.status === 'overdue' ? (
        <Callout tone="red" icon={Receipt} title={`${invoice.daysPastDue} days past due`} className="mb-20">
          Balance {money(invoice.balance)} · {customer?.terms?.toUpperCase()}. Contact {contact?.name || 'the account'} to chase payment.
        </Callout>
      ) : null}

      <div className="split-wide">
        <div className="invoice-sheet" id="invoice-sheet">
          <div className="row between" style={{ alignItems: 'flex-start' }}>
            <div>
              <div className="row gap-10">
                <BrandMark size={40} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-xl)' }}>3Lumpers Services LLC</div>
                  <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Lumper labor &amp; dock services</div>
                </div>
              </div>
              <div className="dim mt-12" style={{ fontSize: 'var(--fs-sm)' }}>
                7800 NW 74th Ave<br />Medley, FL 33166<br />EIN 88-3174402
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="label">Invoice</div>
              <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700 }}>{invoice.code}</div>
              <div className="dim mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
                Issued {dateLabel(invoice.issuedAt, { year: true })}<br />
                Due {dateLabel(invoice.dueAt, { year: true })}<br />
                Terms {customer?.terms?.toUpperCase()}
              </div>
              {invoice.poNumber ? <div className="dim mt-8" style={{ fontSize: 'var(--fs-sm)' }}>PO {invoice.poNumber}</div> : null}
            </div>
          </div>

          <div className="divider" style={{ background: '#e6e9ee', margin: '26px 0' }} />

          <div className="row between" style={{ alignItems: 'flex-start', gap: 30 }}>
            <div>
              <div className="label">Bill to</div>
              <div style={{ fontWeight: 650, fontSize: 'var(--fs-lg)' }}>{customer?.name}</div>
              <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                {customer?.address}<br />
                {customer?.city}, {customer?.state} {customer?.zip}<br />
                {customer?.billingEmail}
              </div>
            </div>
            <div>
              <div className="label">Attention</div>
              <div style={{ fontWeight: 600 }}>{contact?.name || 'Accounts payable'}</div>
              <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                {contact?.title}<br />
                {contact?.email}<br />
                {phone(contact?.phone)}
              </div>
            </div>
            <div>
              <div className="label">Service period</div>
              <div style={{ fontWeight: 600 }}>
                {dateLabel(invoice.periodStart)} – {dateLabel(invoice.periodEnd)}
              </div>
              <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{invoice.jobsCount} jobs</div>
            </div>
          </div>

          <table className="mt-28">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 70, textAlign: 'right' }}>Qty</th>
                <th style={{ width: 70 }}>Unit</th>
                <th style={{ width: 100, textAlign: 'right' }}>Rate</th>
                <th style={{ width: 110, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.description}
                    {item.siteName ? <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{item.siteName}, {item.siteCity}</div> : null}
                  </td>
                  <td style={{ textAlign: 'right' }}>{num(item.qty, 0)}</td>
                  <td>{item.unit}</td>
                  <td style={{ textAlign: 'right' }}>{money(item.unitPrice, { decimals: 2 })}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(item.amount, { decimals: 2 })}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ textAlign: 'right', color: '#616a78' }}>Subtotal</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(invoice.subtotal, { decimals: 2 })}</td>
              </tr>
              {Number(invoice.accessorials) ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', color: '#616a78' }}>Extras</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(invoice.accessorials, { decimals: 2 })}</td>
                </tr>
              ) : null}
              <tr>
                <td colSpan={4} style={{ textAlign: 'right', color: '#616a78' }}>Tax</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(invoice.tax, { decimals: 2 })}</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ textAlign: 'right', borderTop: '2px solid #e6e9ee' }}>
                  <strong>Total due</strong>
                </td>
                <td style={{ textAlign: 'right', borderTop: '2px solid #e6e9ee' }}>
                  <span className="invoice-total">{money(invoice.total, { decimals: 2 })}</span>
                </td>
              </tr>
              {Number(invoice.balance) !== Number(invoice.total) ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', color: '#616a78' }}>Balance remaining</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(invoice.balance, { decimals: 2 })}</td>
                </tr>
              ) : null}
            </tfoot>
          </table>

          <div className="mt-28" style={{ fontSize: 'var(--fs-sm)', color: '#616a78' }}>
            <strong style={{ color: '#16181d' }}>Remit payment</strong>
            <div>ACH or check payable to 3Lumpers Services LLC. Reference {invoice.code} with your payment.</div>
            {invoice.notes ? <div style={{ marginTop: 10 }}>{invoice.notes}</div> : null}
          </div>
        </div>

        <div className="col gap-20 no-print">
          <Card>
            <CardHead title="Summary" icon={Receipt} />
            <CardBody>
              <KeyValue label="Subtotal">{money(invoice.subtotal)}</KeyValue>
              <KeyValue label="Extras">{money(invoice.accessorials)}</KeyValue>
              <KeyValue label="Total">{money(invoice.total)}</KeyValue>
              <div className="divider" />
              <KeyValue label="Balance">
                <span style={{ fontWeight: 660, color: Number(invoice.balance) > 0 ? 'var(--amber)' : 'var(--green)' }}>
                  {money(invoice.balance)}
                </span>
              </KeyValue>
              <KeyValue label="Payments received">{money(Number(invoice.total) - Number(invoice.balance))}</KeyValue>
              <KeyValue label="Jobs on this invoice">{num(invoice.jobsCount)}</KeyValue>
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Payments" icon={Wallet} />
            <CardBody className="col gap-10">
              {payments.length ? (
                payments.map((payment) => (
                  <div key={payment.id} className="row between gap-10">
                    <span className="col gap-2">
                      <span className="row gap-6">
                        <Badge tone="green" size="sm">{payment.method}</Badge>
                        <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(payment.amount)}</span>
                      </span>
                      <span className="dimmer" style={{ fontSize: 10 }}>
                        {dateLabel(payment.receivedAt)} · {payment.reference || 'no reference'}
                      </span>
                    </span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{payment.recordedBy}</span>
                  </div>
                ))
              ) : (
                <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>No payments recorded yet.</span>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="The jobs on this invoice" subtitle={`${items.length} lines`} icon={Receipt} />
            <CardBody className="col gap-8" style={{ maxHeight: 300, overflow: 'auto' }}>
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={item.jobId ? `/app/jobs/${item.jobId}` : '#'}
                  className="row between gap-10"
                  style={{ fontSize: 'var(--fs-sm)' }}
                >
                  <span className="truncate">{item.jobCode || item.description}</span>
                  <span className="num dim">{money(item.amount)}</span>
                </Link>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>

      <RecordPaymentModal
        open={payOpen}
        invoice={invoice}
        onClose={() => setPayOpen(false)}
        onSaved={() => {
          setPayOpen(false);
          refresh();
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await api.del(`/invoices/${id}`);
            toast.info('Invoice deleted');
            navigate('/app/invoices');
          } catch (err) {
            toast.err('Could not delete', err.message);
          }
        }}
        title={`Delete ${invoice.code}?`}
        body="This deletes the invoice and its lines. The jobs themselves stay on file and can be billed again."
        confirmLabel="Delete invoice"
        danger
      />
    </>
  );
}

function RecordPaymentModal({ open, invoice, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ amount: '', method: 'ach', reference: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const amount = form.amount === '' ? Number(invoice?.balance || 0) : Number(form.amount);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a payment"
      subtitle={`${invoice?.code} · balance ${money(invoice?.balance)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/invoices/${invoice.id}/payment`, {
                  amount,
                  method: form.method,
                  reference: form.reference || undefined,
                  notes: form.notes || undefined,
                });
                toast.ok(`${money(amount)} recorded`);
                setForm({ amount: '', method: 'ach', reference: '', notes: '' });
                onSaved();
              } catch (err) {
                toast.err('Could not record the payment', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Record payment
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Amount" hint={`Leave blank to settle the full balance (${money(invoice?.balance)})`}>
          <Input className="mono" type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} placeholder={String(invoice?.balance || '')} />
        </Field>
        <Field label="Method">
          <Select
            value={form.method}
            onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))}
            options={[
              { value: 'ach', label: 'ACH' },
              { value: 'check', label: 'Check' },
              { value: 'wire', label: 'Wire' },
              { value: 'card', label: 'Card' },
            ]}
          />
        </Field>
        <Field label="Reference" className="span-2">
          <Input value={form.reference} onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))} placeholder="Check #10428 / ACH trace" />
        </Field>
        <Field label="Notes" className="span-2">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
        </Field>
      </div>
      <Callout tone={amount >= Number(invoice?.balance || 0) ? 'green' : 'amber'} className="mt-16" icon={Wallet}>
        {amount >= Number(invoice?.balance || 0)
          ? 'This settles the invoice in full.'
          : `Partial payment — ${money(Number(invoice?.balance || 0) - amount)} would remain outstanding.`}
      </Callout>
    </Modal>
  );
}
